import React, { MouseEvent, ChangeEvent, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ShippingLabelTypes, generateParcel, getParcelById, getParcelTypesByCustomer } from 'src/api/shippingPortal';
import './EditLabelView.scss';
import { getCapitalized, getShipmentTypeFromContext, getUserEmailFromToken } from 'src/util/helpers';
import { useSnackbar, SnackbarKey } from 'notistack';
import { ShippingPortalLocation, ShippingPortalParcel, Permissions } from '@lanterds/types';
import { ShippingPortalLabelSizes } from '@lanterds/types/build/shippingPortal';
import dayjs from 'dayjs';
import Header from 'src/components/common/Header/Header';
import { PAGE_INFO, SELECTED_SHIPPING_PORTAL_TYPE_LOCAL_STORAGE_KEY } from 'src/util/constants';
import EmbeddedError from 'src/components/common/EmbeddedError/EmbeddedError';
import { AddressPicker } from 'src/components/common/AddressPicker/AddressPicker';
import { LocationTypes } from '../ShippingPortal/types';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import { isAuthorized } from 'src/util/Permissions';

type BlankForm = Omit<ShippingPortalParcel, 'origin' | 'destination'> & {
    origin: ShippingPortalLocation | null;
    destination: ShippingPortalLocation | null;
};

export default function EditLabelView() {
    const fmtpNavigate = useFmtpNavigate();
    const { id } = useParams();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const saveSnackbarRef = useRef<SnackbarKey | null>(null);
    const loadSnackbarRef = useRef<SnackbarKey | null>(null);
    const newLabelType = getShipmentTypeFromContext(SELECTED_SHIPPING_PORTAL_TYPE_LOCAL_STORAGE_KEY, 'type');
    const validNewLabelType = newLabelType && Object.values(ShippingLabelTypes).includes(newLabelType as ShippingLabelTypes);
    const userId = getUserEmailFromToken();
    const isUnrestrictedSearchAuthorized = isAuthorized(userId, Permissions.shippingPortal_unfilteredDestinations);

    const blankForm: BlankForm = {
        id: '-1',
        shippingType: newLabelType as ShippingLabelTypes,
        manifest: null,
        // Need default value of origin and destination to specifically be null so the fields properly clear
        origin: null,
        destination: null,
        hazardous: 'N',
        weight: 0,
        weightUnit: 'lbs',
        reference: '',
        parcelNumber: '',
        fmtpCustomerId: String(-1),
        numberOfPieces: 1,
        partNumber: '',
        labelSize: ShippingPortalLabelSizes.LARGE,
        parcelType: 'LOOSE',
        createdBy: '',
        createdOn: '',
    };

    const [formValue, setFormValue] = useState<BlankForm>({ ...blankForm });
    const [numberOfParcels, setNumberOfParcels] = useState(1);
    // TODO: Set this to ReactElement[] and deal with errors
    const [parcelTypeOptions, setParcelTypeOptions] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isLoadingParcelTypes, setIsLoadingParcelTypes] = useState(false);
    const [filterByOrigin, setFilterByOrigin] = useState(true);

    const isManifested = !!formValue.manifest;
    const isNewLabel = id === 'new';
    const enableQueries = !isNewLabel && !!id;
    const { data: parcel, isFetching: isParcelByIdFetching } = getParcelById(String(id), enableQueries);

    useEffect(() => {
        if (parcel && !isParcelByIdFetching) {
            setFormValue(parcel);
        }
    }, [id, parcel, isParcelByIdFetching]);

    const enableAutoPopulate = isNewLabel || (!isParcelByIdFetching && formValue.id !== '-1');

    useEffect(() => {
        const customerId = formValue.origin?.fmtpCustomerId ?? formValue.destination?.fmtpCustomerId ?? null;
        if (customerId && customerId !== 'undefined' && parcelTypeOptions.length < 1) {
            setIsLoadingParcelTypes(true);
            getParcelTypesByCustomer(customerId).then((response: string[]) => {
                const mappedTypes = response.map((t) => {
                    return {
                        key: t,
                        val: t,
                    };
                });

                setParcelTypeOptions(
                    mappedTypes.map((type) => {
                        return (
                            <MenuItem disableRipple key={type.key} value={type.val}>
                                {type.val}
                            </MenuItem>
                        );
                    }),
                );
                setIsLoadingParcelTypes(false);
            });
        }
    }, [formValue.origin?.fmtpCustomerId, formValue.destination?.fmtpCustomerId]);

    useEffect(() => {
        const { parcelType } = formValue;
        if (parcelTypeOptions.length && (parcelType === '' || !parcelType)) {
            onChangeInput({
                target: { name: 'parcelType', value: parcelTypeOptions[0].val },
            } as ChangeEvent<HTMLSelectElement>);
        }
    }, [parcelTypeOptions]);

    useEffect(() => {
        if (isParcelByIdFetching) {
            loadSnackbarRef.current = enqueueSnackbar('Loading parcel…', {
                variant: 'loading',
                persist: true,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        } else if (loadSnackbarRef.current) {
            closeSnackbar(loadSnackbarRef.current);
            loadSnackbarRef.current = null;
        }
    }, [isParcelByIdFetching, enqueueSnackbar, closeSnackbar]);

    const onLocationChangeInput = (value: ShippingPortalLocation | ShippingPortalLocation[] | null, name: string) => {
        if (
            (!formValue.origin && name === 'destination' && value === null) ||
            (!formValue.destination && name === 'origin' && value === null)
        ) {
            setParcelTypeOptions([]);
        }
        setFormValue({ ...formValue, [name]: value });
    };

    const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value }: { name: string; value: string | number | ShippingPortalLocation } = e.target;

        switch (name) {
            case 'numberOfPieces':
                // This regex checks whether the value contain only digits
                if (!/^\d+$/.test(value) || !value) {
                    setFormValue({ ...formValue, [name]: 1 });
                } else {
                    setFormValue({ ...formValue, [name]: Number(value) });
                }
                break;
            default:
                setFormValue({ ...formValue, [name]: value });
        }
    };

    const onClickGenerateLabels = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const parcelsToGenerate = [] as ShippingPortalParcel[];

        if (!formValue.destination || !formValue.origin) {
            alert('Origin and Destination are Required');
            return;
        }

        saveSnackbarRef.current = enqueueSnackbar('Saving parcel…', {
            variant: 'loading',
            persist: true,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        setIsSaving(true);

        for (let i = 0; i < numberOfParcels; i++) {
            parcelsToGenerate.push({
                id: String(isNewLabel ? -1 : (id ?? -1)),
                manifest: formValue.manifest,
                parcelNumber: isNewLabel ? '-1' : formValue.parcelNumber,
                destination: formValue.destination,
                origin: formValue.origin,
                weight: Number(formValue.weight),
                weightUnit: formValue.weightUnit,
                reference: formValue.reference,
                shippingType: formValue.shippingType,
                // Always send 'No' to the backend - work item 17718
                // hazardous: formValue.hazardous,
                hazardous: 'N',
                fmtpCustomerId: formValue.origin.fmtpCustomerId,
                numberOfPieces: Number(formValue.numberOfPieces),
                partNumber: formValue.partNumber,
                parcelType: formValue.parcelType,
                labelSize: formValue.labelSize,
                createdOn: dayjs().toISOString(),
                createdBy: 'newUser', // TODO replace Created By
            });
        }

        let isError = false;
        setErrorMessage('');

        try {
            const response = await generateParcel(parcelsToGenerate);

            setIsSaving(false);

            if (response) {
                fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${newLabelType}`);
            } else {
                isError = true;
            }
        } catch (error) {
            setIsSaving(false);
            console.error(error);
            isError = true;
        } finally {
            setIsSaving(false);
            if (saveSnackbarRef.current) {
                closeSnackbar(saveSnackbarRef.current);
                saveSnackbarRef.current = null;
            }
        }

        if (isError) {
            enqueueSnackbar('Failed to save parcel. Please try again.', { variant: 'error' });
            setErrorMessage('Failed to save parcel. Please try again.');
        } else {
            enqueueSnackbar('Parcel saved successfully!', { variant: 'success' });
            fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${newLabelType}`);
        }
    };

    const onClickCancel = () => {
        fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${newLabelType}`);
    };

    const onClickClear = () => {
        setFormValue({ ...blankForm });
        setParcelTypeOptions([]);
    };

    const getHeaderText = () => {
        let headerText = '';
        if (isNewLabel) {
            headerText = `Create New ${validNewLabelType ? getCapitalized(newLabelType) : ''}`;
        } else {
            // Wait until we've fetched the parcelNumber to prevent the header from populating in chunks
            if (formValue.parcelNumber) {
                headerText = `Edit Label ${formValue.parcelNumber ? getCapitalized(formValue.parcelNumber) : ''}`;
            }
        }
        return headerText;
    };

    const onFilterByOriginChange = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setFilterByOrigin(checked);
    };

    return (
        <>
            <Box id="edit-label-container">
                <Card id="edit-label-card">
                    <Grid container spacing={2}>
                        <Grid xs={12}>
                            <Header text={getHeaderText()} />
                        </Grid>
                        <Grid xs={12}>
                            <CardContent>
                                <Grid container spacing={2} className="location-search-container">
                                    <Grid sm={5.5} xs={12} container spacing={2}>
                                        <Grid xs={12} container spacing={2} className="origin-label-container">
                                            <Grid xs={isUnrestrictedSearchAuthorized ? 2 : 12}>
                                                <Typography>Origin</Typography>
                                            </Grid>
                                            {isUnrestrictedSearchAuthorized && (
                                                <Grid xs={6} className="filter-by-origin-container">
                                                    <FormControl>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={filterByOrigin}
                                                                    onChange={onFilterByOriginChange}
                                                                    disableRipple
                                                                />
                                                            }
                                                            label="Filter By Origin"
                                                            checked={filterByOrigin}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            )}
                                        </Grid>
                                        <AddressPicker
                                            name={LocationTypes.Origin}
                                            onChangeInput={onLocationChangeInput}
                                            locations={formValue.origin ? [formValue.origin] : []}
                                            context={{
                                                locationType: LocationTypes.Origin,
                                                destination: formValue.destination,
                                                origin: formValue.origin,
                                                searchType: formValue.shippingType,
                                                labelType: formValue.shippingType,
                                                filterByOrigin: filterByOrigin,
                                            }}
                                            disabled={isManifested}
                                            enableAutoPopulate={enableAutoPopulate}
                                            splitNameAndCodeFields={true}
                                        />
                                        <Grid xs={12}>
                                            <Typography>Destination</Typography>
                                        </Grid>
                                        <AddressPicker
                                            name={LocationTypes.Destination}
                                            onChangeInput={onLocationChangeInput}
                                            locations={formValue.destination ? [formValue.destination] : []}
                                            context={{
                                                locationType: LocationTypes.Destination,
                                                origin: formValue.origin,
                                                destination: formValue.destination,
                                                searchType: formValue.shippingType,
                                                labelType: formValue.shippingType,
                                                filterByOrigin: filterByOrigin,
                                            }}
                                            disabled={isManifested}
                                            enableAutoPopulate={enableAutoPopulate}
                                            splitNameAndCodeFields={true}
                                        />
                                    </Grid>
                                    <Grid sm={5.5} xs={12} container spacing={2}>
                                        <Grid xs={12}>
                                            <Typography>Details</Typography>
                                        </Grid>

                                        <Grid sm={6} xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="size-select-label">Label Size</InputLabel>
                                                <Select
                                                    labelId="size-select-label"
                                                    name="labelSize"
                                                    id="label-size"
                                                    fullWidth
                                                    value={formValue.labelSize}
                                                    variant="outlined"
                                                    label="Label Size"
                                                    onChange={onChangeInput}
                                                    disabled={isManifested}
                                                >
                                                    <MenuItem disableRipple value={ShippingPortalLabelSizes.LARGE}>
                                                        {ShippingPortalLabelSizes.LARGE}
                                                    </MenuItem>
                                                    <MenuItem disableRipple value={ShippingPortalLabelSizes.MEDIUM}>
                                                        {ShippingPortalLabelSizes.MEDIUM}
                                                    </MenuItem>
                                                    <MenuItem disableRipple value={ShippingPortalLabelSizes.SMALL}>
                                                        {ShippingPortalLabelSizes.SMALL}
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid sm={6} xs={12}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    id="reference"
                                                    label="Reference"
                                                    variant="outlined"
                                                    name="reference"
                                                    autoComplete="off"
                                                    fullWidth
                                                    onChange={onChangeInput}
                                                    value={formValue.reference}
                                                    disabled={isManifested}
                                                />
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="parcel-type-label">Parcel Type</InputLabel>
                                                <Select
                                                    name="parcelType"
                                                    variant="outlined"
                                                    labelId="parcel-type-label"
                                                    label="Parcel Type"
                                                    onChange={onChangeInput}
                                                    fullWidth
                                                    placeholder={formValue.parcelType}
                                                    defaultValue={formValue.parcelType}
                                                    value={formValue.parcelType}
                                                    disabled={isManifested || !formValue.destination}
                                                    endAdornment={
                                                        <>
                                                            {isLoadingParcelTypes ? (
                                                                <CircularProgress
                                                                    className="parcel-type-loading"
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            ) : null}
                                                        </>
                                                    }
                                                >
                                                    {parcelTypeOptions}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid sm={6} xs={12}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    name="numberOfPieces"
                                                    type="number"
                                                    variant="outlined"
                                                    label="Number of Pieces"
                                                    onChange={onChangeInput}
                                                    fullWidth
                                                    value={Number(formValue.numberOfPieces).toString()}
                                                    InputProps={{
                                                        inputProps: { min: 1 },
                                                    }}
                                                    disabled={isManifested}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid sm={6} xs={12}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    name="partNumber"
                                                    variant="outlined"
                                                    label="Part Number"
                                                    onChange={onChangeInput}
                                                    fullWidth
                                                    value={formValue.partNumber}
                                                    disabled={isManifested}
                                                    autoComplete="off"
                                                />
                                            </FormControl>
                                        </Grid>

                                        <Grid sm={6} xs={12}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    name="weight"
                                                    type="number"
                                                    variant="outlined"
                                                    label="Weight"
                                                    onChange={onChangeInput}
                                                    fullWidth
                                                    value={Number(formValue.weight).toString()}
                                                    InputProps={{
                                                        inputProps: { min: 0 },
                                                    }}
                                                    disabled={isManifested}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid sm={6} xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="weight-unit-label">Unit</InputLabel>
                                                <Select
                                                    labelId="weight-unit-label"
                                                    name="weightUnit"
                                                    id="weight-unit"
                                                    value={formValue.weightUnit}
                                                    variant="outlined"
                                                    label="Unit"
                                                    onChange={onChangeInput}
                                                    disabled={isManifested}
                                                >
                                                    <MenuItem disableRipple value="lbs">
                                                        pounds
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Hide input - work item 17718 */}
                                        <Grid xs={12} display="none">
                                            <FormControl fullWidth>
                                                <InputLabel id="hazardous-label">Hazardous?</InputLabel>
                                                <Select
                                                    labelId="hazardous-label"
                                                    name="hazardous"
                                                    id="hazardous"
                                                    value={formValue.hazardous}
                                                    variant="outlined"
                                                    label="Hazardous?"
                                                    onChange={onChangeInput}
                                                    disabled={isManifested}
                                                >
                                                    <MenuItem disableRipple value="Y">
                                                        Yes
                                                    </MenuItem>
                                                    <MenuItem disableRipple value="N" selected={true}>
                                                        No
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {/* Using empty element to help align grids */}
                                        <Grid xs={12}>&nbsp;</Grid>
                                    </Grid>
                                </Grid>
                                {/* TODO: Upgrade to snackbars */}
                                <Grid xs={12} className="error-container">
                                    {errorMessage && (
                                        <Box className="error-message">
                                            <EmbeddedError
                                                message={errorMessage}
                                                errorCode="SHU007"
                                                showContactSupport={true}
                                            />
                                        </Box>
                                    )}
                                    <CardActions>
                                        {!isManifested ? (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    size="medium"
                                                    onClick={onClickGenerateLabels}
                                                    disabled={isSaving}
                                                >
                                                    {isSaving ? 'Saving...' : 'Save'}
                                                </Button>

                                                <Button variant="outlined" onClick={onClickCancel}>
                                                    Cancel
                                                </Button>

                                                {isNewLabel && (
                                                    <Button variant="text" onClick={onClickClear}>
                                                        Clear Form
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <Button variant="contained" onClick={onClickCancel}>
                                                Go Back
                                            </Button>
                                        )}
                                    </CardActions>
                                </Grid>
                            </CardContent>
                        </Grid>
                    </Grid>
                </Card>
            </Box>
        </>
    );
}
